import { CacheError, requestToPromise, runTransaction, wrapCacheError } from "./cache-database";
import type { CONTENT_KEY_PATHS, ContentStoreName, StoreName } from "./cache-schema";

/** The fields of `T` whose value is a string: the only fields a content store may be keyed by. */
export type KeyField< T > = { [ P in keyof T ]-?: T[ P ] extends string ? P : never }[ keyof T ] & string;

/** The content stores whose schema keyPath is `K`: a store can only be declared with the field it is keyed by on disk. */
export type StoreKeyedBy< K extends string > = { [ N in ContentStoreName ]: typeof CONTENT_KEY_PATHS[ N ] extends K ? N : never }[ ContentStoreName ];

/**
 * A content store. Every record is keyed by its own `keyField` (content by `contentfulId`, dependencies by `id`,
 * sector history by `dt`), which is also the store's in-line IndexedDB keyPath (CONTENT_KEY_PATHS, tmi-5q7), so a
 * row can never sit under a key that disagrees with the record.
 */
export interface CacheStore< T > {
    readonly name: StoreName;
    readonly keyField: string;
    /** Every value, in ascending key order. */
    all(): Promise< T[] >;
    keys(): Promise< string[] >;
    /** `undefined` when the key is absent. */
    get( key: string ): Promise< T | undefined >;
    count(): Promise< number >;
    isEmpty(): Promise< boolean >;
    /** Rejects with a CacheError, writing nothing, when `value[ keyField ]` is not a non-empty string. */
    put( value: T ): Promise< void >;
    /** One readwrite transaction: every record lands or none does, including when one lacks its key. */
    putMany( values: ReadonlyArray< T > ): Promise< void >;
    /** One readwrite transaction. Absent keys are ignored. */
    deleteMany( keys: ReadonlyArray< string > ): Promise< void >;
    clear(): Promise< void >;
}

/** Sync bookkeeping: cooldown timestamps, ETags, the owner stamp. Values are primitives, so keys stay explicit. */
export interface StateStore {
    readonly name: StoreName;
    /** `undefined` when the key is absent. `V` names the type the caller stored under that key. */
    get< V = unknown >( key: string ): Promise< V | undefined >;
    put( key: string, value: unknown ): Promise< void >;
    delete( key: string ): Promise< void >;
}

/** Throws the CacheError both the real store and the fake raise for a record without a usable key. */
export function readKey( store: StoreName, operation: string, keyField: string, value: unknown ): string {
    const key = ( value as Record< string, unknown > | null | undefined )?.[ keyField ];
    if ( typeof key !== 'string' || 0 === key.length ) {
        throw new CacheError( `[ cache ] ${ store }.${ operation } rejected a record: "${ keyField }" must be a non-empty string, got ${ typeof key === 'string' ? 'an empty string' : typeof key }` );
    }
    return key;
}

function accessors( name: StoreName ) {
    // The one CacheError wrapper (tmi-cya #9): a CacheError — including readKey's rejection — passes through unchanged.
    const wrap = < R >( operation: string, work: () => Promise< R > ): Promise< R > => wrapCacheError( `${ name }.${ operation }`, work );

    const read = < R >( operation: string, request: ( store: IDBObjectStore ) => IDBRequest< R > ): Promise< R > =>
        wrap( operation, () => runTransaction( [ name ], 'readonly', tx => requestToPromise( request( tx.objectStore( name ) ) ) ) );

    const write = ( operation: string, apply: ( store: IDBObjectStore ) => void ): Promise< void > =>
        wrap( operation, () => runTransaction( [ name ], 'readwrite', tx => { apply( tx.objectStore( name ) ); } ) );

    return { wrap, read, write };
}

export function createStore< T, K extends KeyField< T > >( name: StoreKeyedBy< K >, keyField: K ): CacheStore< T > {
    const { wrap, read, write } = accessors( name );

    return {
        name,
        keyField,
        all: () => read( 'all', store => store.getAll() as IDBRequest< T[] > ),
        // Keys are strings by construction: every write derives them through `readKey`. IDBValidKey[] is narrowed here once.
        keys: () => read( 'keys', store => store.getAllKeys() as IDBRequest< string[] > ),
        get: key => read( 'get', store => store.get( key ) as IDBRequest< T | undefined > ),
        count: () => read( 'count', store => store.count() ),
        isEmpty: async () => 0 === await read( 'isEmpty', store => store.count() ),
        // The store's keyPath reads `keyField` itself, so no key argument is passed (one would be a DataError). readKey
        // still runs first: IndexedDB would accept a numeric key, and the fake must fail with the same CacheError.
        put: value => wrap( 'put', async () => {
            readKey( name, 'put', keyField, value );
            await write( 'put', store => { store.put( value ); } );
        } ),
        putMany: values => {
            if ( 0 === values.length ) {
                return Promise.resolve();
            }
            // Every key is read before the transaction opens: a bad record rejects the batch with nothing written.
            return wrap( 'putMany', async () => {
                for ( const value of values ) readKey( name, 'putMany', keyField, value );
                await write( 'putMany', store => { for ( const value of values ) store.put( value ); } );
            } );
        },
        deleteMany: keys => 0 === keys.length
            ? Promise.resolve()
            : write( 'deleteMany', store => { for ( const key of keys ) store.delete( key ); } ),
        clear: () => write( 'clear', store => { store.clear(); } )
    };
}

export function createStateStore( name: StoreName ): StateStore {
    const { read, write } = accessors( name );

    return {
        name,
        get: < V = unknown >( key: string ) => read( 'get', store => store.get( key ) as IDBRequest< V | undefined > ),
        put: ( key, value ) => write( 'put', store => { store.put( value, key ); } ),
        delete: key => write( 'delete', store => { store.delete( key ); } )
    };
}
